/**
 * IFT mobile screens: Tree, People, Moments, More.
 *
 * These screens wrap or embed existing desktop pages inside the IFT mobile
 * chrome, so we don't re-implement business logic. Where a desktop page is
 * complex (family tree canvas), we render a mobile-tuned landing that
 * deep-links to the desktop route on tap — future iterations will bring the
 * canvas natively into the shell.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Trees,
  Users as PeopleIcon,
  Camera,
  Search,
  Settings,
  LogOut,
  Globe,
  BookOpen,
  ChevronRight,
  Feather,
  BadgeCheck,
  Loader2,
  MapPin,
} from "lucide-react";
import MobileIftShell from "./MobileIftShell";

const P = {
  saffron:   "#C2410C",
  saffronBg: "#FFF7ED",
  parchment: "#FBE9C6",
  ink:       "#431a04",
  inkSoft:   "#6B4A2E",
  line:      "rgba(194,65,12,0.15)",
  cardBg:    "#fff",
};

function SectionHeader({ title, actionLabel, actionTo, testid }) {
  return (
    <div className="flex items-center justify-between mb-2" data-testid={testid}>
      <h2 className="text-sm font-semibold uppercase tracking-[0.12em]" style={{ color: P.ink }}>{title}</h2>
      {actionTo && (
        <Link to={actionTo} className="text-[11px] font-semibold" style={{ color: P.saffron }}>{actionLabel || "See all"}</Link>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* TREE                                                                 */
/* ────────────────────────────────────────────────────────────────── */

export function MobileIftTree() {
  const { user } = useAuth();
  const [trees, setTrees] = useState(null);

  useEffect(() => {
    if (!user) { setTrees([]); return; }
    http.get("/family-trees?scope=mine&limit=20")
      .then((r) => setTrees(r.data.items || []))
      .catch(() => setTrees([]));
  }, [user]);

  return (
    <MobileIftShell>
      <div className="px-4 pt-4 pb-6" data-testid="ift-mtree">
        <h1 className="text-2xl mb-4" style={{ color: P.ink, fontFamily: "'Playfair Display', Georgia, serif" }}>Your Family Trees</h1>

        {!user && (
          <div className="rounded-2xl p-6 text-center" style={{ background: P.cardBg, border: `1px solid ${P.line}` }}>
            <Trees className="w-10 h-10 mx-auto mb-3" style={{ color: P.saffron }} />
            <p className="text-sm mb-4" style={{ color: P.inkSoft }}>Sign in to see your family tree.</p>
            <Link to="/login" className="inline-block px-5 py-2 rounded-full text-sm font-semibold" style={{ background: P.saffron, color: "#fff" }} data-testid="ift-mtree-signin">
              Sign in
            </Link>
          </div>
        )}

        {user && trees === null && (
          <div className="py-8 flex items-center justify-center text-xs" style={{ color: P.inkSoft }}>
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your trees…
          </div>
        )}

        {user && trees && trees.length === 0 && (
          <div className="rounded-2xl p-6 text-center" style={{ background: P.cardBg, border: `1px solid ${P.line}` }}>
            <p className="text-sm mb-4" style={{ color: P.inkSoft }}>You aren't part of any family tree yet.</p>
            <Link to="/family-trees/new" className="inline-block px-5 py-2 rounded-full text-sm font-semibold" style={{ background: P.saffron, color: "#fff" }} data-testid="ift-mtree-new">
              Start your family tree
            </Link>
          </div>
        )}

        {user && trees && trees.length > 0 && (
          <div className="rounded-2xl px-4" style={{ background: P.cardBg, border: `1px solid ${P.line}` }}>
            {trees.map((t, i) => (
              <Link
                key={t.id || i}
                to={`/family/${t.slug || t.id}`}
                className="flex items-center gap-3 py-3"
                style={{ borderBottom: i < trees.length - 1 ? `1px solid ${P.line}` : "none" }}
                data-testid={`ift-mtree-item-${i}`}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: P.parchment, color: P.saffron }}>
                  <Trees className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: P.ink }}>{t.name}</div>
                  <div className="text-[11px] truncate" style={{ color: P.inkSoft }}>
                    {t.member_count || 0} members
                    {t.country ? ` · ${t.country}` : ""}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: P.inkSoft }} />
              </Link>
            ))}
          </div>
        )}

        {/* "Where am I?" call-to-action */}
        {user && trees && trees.length > 0 && (
          <div className="mt-4">
            <Link
              to={`/family/${trees[0].slug || trees[0].id}?whereami=1`}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold"
              style={{ background: P.saffron, color: "#fff" }}
              data-testid="ift-mtree-whereami"
            >
              <MapPin className="w-4 h-4" /> Show my place in the family
            </Link>
          </div>
        )}
      </div>
    </MobileIftShell>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* PEOPLE                                                               */
/* ────────────────────────────────────────────────────────────────── */

export function MobileIftPeople() {
  const [query, setQuery] = useState("");
  const nav = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    nav(`/search?q=${encodeURIComponent(query.trim())}&kind=people`);
  };

  return (
    <MobileIftShell>
      <div className="px-4 pt-4 pb-6" data-testid="ift-mpeople">
        <h1 className="text-2xl mb-4" style={{ color: P.ink, fontFamily: "'Playfair Display', Georgia, serif" }}>People</h1>

        <form onSubmit={submit} className="mb-4">
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-2xl"
            style={{ background: P.cardBg, border: `1px solid ${P.line}` }}
          >
            <Search className="w-4 h-4" style={{ color: P.inkSoft }} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search ancestors, cousins, memorials…"
              data-testid="ift-mpeople-search"
              className="flex-1 outline-none text-sm bg-transparent"
              style={{ color: P.ink }}
            />
          </div>
        </form>

        <div className="rounded-2xl px-4" style={{ background: P.cardBg, border: `1px solid ${P.line}` }}>
          <Link to="/celebrate/browse" className="flex items-center gap-3 py-3" style={{ borderBottom: `1px solid ${P.line}` }} data-testid="ift-mpeople-memorials">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: P.parchment, color: P.saffron }}>
              <Feather className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium" style={{ color: P.ink }}>Browse Memorials</div>
              <div className="text-[11px]" style={{ color: P.inkSoft }}>Recent tributes across every family</div>
            </div>
            <ChevronRight className="w-4 h-4" style={{ color: P.inkSoft }} />
          </Link>
          <Link to="/famous-indian-families" className="flex items-center gap-3 py-3" style={{ borderBottom: `1px solid ${P.line}` }} data-testid="ift-mpeople-famous">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: P.parchment, color: P.saffron }}>
              <BadgeCheck className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium" style={{ color: P.ink }}>Famous Indian Families</div>
              <div className="text-[11px]" style={{ color: P.inkSoft }}>Discover well-documented lineages</div>
            </div>
            <ChevronRight className="w-4 h-4" style={{ color: P.inkSoft }} />
          </Link>
          <Link to="/family-trees?visibility=public" className="flex items-center gap-3 py-3" data-testid="ift-mpeople-public">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: P.parchment, color: P.saffron }}>
              <PeopleIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium" style={{ color: P.ink }}>Public Family Trees</div>
              <div className="text-[11px]" style={{ color: P.inkSoft }}>Trees families have chosen to share</div>
            </div>
            <ChevronRight className="w-4 h-4" style={{ color: P.inkSoft }} />
          </Link>
        </div>
      </div>
    </MobileIftShell>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* MOMENTS                                                              */
/* ────────────────────────────────────────────────────────────────── */

export function MobileIftMoments() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);

  useEffect(() => {
    // Best-effort: recent shared memories across the user's families.
    http.get("/family-recap/recent?limit=20")
      .then((r) => setItems(r.data.items || r.data || []))
      .catch(() => setItems([]));
  }, [user]);

  return (
    <MobileIftShell>
      <div className="px-4 pt-4 pb-6" data-testid="ift-mmoments">
        <h1 className="text-2xl mb-4" style={{ color: P.ink, fontFamily: "'Playfair Display', Georgia, serif" }}>Family Moments</h1>

        {items === null && (
          <div className="py-8 flex items-center justify-center text-xs" style={{ color: P.inkSoft }}>
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading moments…
          </div>
        )}
        {items && items.length === 0 && (
          <div className="rounded-2xl p-6 text-center" style={{ background: P.cardBg, border: `1px solid ${P.line}` }}>
            <Camera className="w-10 h-10 mx-auto mb-3" style={{ color: P.saffron }} />
            <p className="text-sm mb-4" style={{ color: P.inkSoft }}>Your family memories will appear here — photos, stories and new members.</p>
            <Link to="/celebrate/submit" className="inline-block px-5 py-2 rounded-full text-sm font-semibold" style={{ background: P.saffron, color: "#fff" }} data-testid="ift-mmoments-submit">
              Add a memory
            </Link>
          </div>
        )}
        {items && items.length > 0 && (
          <div className="space-y-3">
            {items.map((r, i) => (
              <Link
                key={r.id || i}
                to={r.href || (r.tree_slug ? `/family/${r.tree_slug}` : "/m")}
                className="flex gap-3 p-3 rounded-2xl"
                style={{ background: P.cardBg, border: `1px solid ${P.line}` }}
                data-testid={`ift-mmoments-item-${i}`}
              >
                <div className="w-14 h-14 rounded-xl flex-shrink-0" style={{ background: P.parchment }}>
                  {r.image_url && <img src={r.image_url} alt="" className="w-full h-full rounded-xl object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: P.ink }}>{r.title || r.description || "Family moment"}</div>
                  <div className="text-[11px] mt-0.5" style={{ color: P.inkSoft }}>{r.by || r.actor_name || r.tree_name || ""}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </MobileIftShell>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* MORE                                                                 */
/* ────────────────────────────────────────────────────────────────── */

export function MobileIftMore() {
  const { user, logout } = useAuth();
  const isAdmin = !!(user && (user.is_admin || user.platform_role === "super_admin"));

  const items = [
    { to: "/dashboard",           label: "My dashboard",     Icon: BookOpen,   testid: "ift-mmore-dashboard" },
    { to: "/family-trees",        label: "All family trees", Icon: Trees,      testid: "ift-mmore-trees" },
    { to: "/celebrate/browse",    label: "Browse memorials", Icon: Feather,    testid: "ift-mmore-memorials" },
    ...(isAdmin ? [{ to: "/admin/memorials/quick", label: "Admin · Add Memorial", Icon: Feather, testid: "ift-mmore-admin-quick-memorial" }] : []),
    { to: "/care/signup",         label: "Care providers",   Icon: BadgeCheck, testid: "ift-mmore-care" },
    { to: "/articles",            label: "Articles & Heritage", Icon: BookOpen,testid: "ift-mmore-articles" },
    { to: "/indian-family-tree/story", label: "About IFT",   Icon: Globe,      testid: "ift-mmore-about" },
    { to: "/policies/ift-privacy-policy", label: "Privacy",  Icon: Settings,   testid: "ift-mmore-privacy" },
  ];

  return (
    <MobileIftShell>
      <div className="px-4 pt-4 pb-6" data-testid="ift-mmore">
        <h1 className="text-2xl mb-4" style={{ color: P.ink, fontFamily: "'Playfair Display', Georgia, serif" }}>More</h1>

        {user && (
          <div className="rounded-2xl p-4 mb-4 flex items-center gap-3" style={{ background: P.cardBg, border: `1px solid ${P.line}` }} data-testid="ift-mmore-me">
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: P.saffron, color: "#fff", fontWeight: 700 }}>
              {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: P.ink }}>{user.name || "Signed in"}</div>
              <div className="text-[11px] truncate" style={{ color: P.inkSoft }}>{user.email || user.phone_e164 || ""}</div>
            </div>
          </div>
        )}

        <div className="rounded-2xl px-4" style={{ background: P.cardBg, border: `1px solid ${P.line}` }}>
          {items.map((it, i) => (
            <Link
              key={it.to}
              to={it.to}
              className="flex items-center gap-3 py-3"
              style={{ borderBottom: i < items.length - 1 ? `1px solid ${P.line}` : "none" }}
              data-testid={it.testid}
            >
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: P.parchment, color: P.saffron }}>
                <it.Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 text-sm" style={{ color: P.ink }}>{it.label}</div>
              <ChevronRight className="w-4 h-4" style={{ color: P.inkSoft }} />
            </Link>
          ))}
        </div>

        {user && (
          <button
            onClick={() => logout && logout()}
            className="w-full mt-4 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold"
            style={{ background: "transparent", color: P.saffron, border: `1px solid ${P.line}` }}
            data-testid="ift-mmore-logout"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        )}
      </div>
    </MobileIftShell>
  );
}
